import { useMemo, useState } from 'react'
import { Alert, App, Button, Card, Select, Table, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { LockOutlined, PlusOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { usePermissions } from '@/shared/auth/usePermissions'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { EmptyState } from '@/shared/ui/EmptyState'
import { PageHeader } from '@/shared/ui/PageHeader'
import { RowActions } from '@/shared/ui/table/RowActions'
import { FinanceExportCard } from '@/features/finance/FinanceExportCard'
import { PeriodCloseModal } from '@/features/finance/PeriodCloseModal'
import { PeriodCreateModal } from '@/features/finance/PeriodCreateModal'
import { ReportFilterBar } from '@/features/finance/ReportFilterBar'
import { ReportTableEmpty } from '@/features/finance/ReportTableEmpty'
import { matchesSearch, matchesValues, useLocalListState } from '@/shared/lib/localList'
import {
  PERIOD_STATES,
  deletePeriod,
  fetchPeriods,
  isPeriodClosed,
  type Period,
} from '@/features/finance/financeApi'
import { periodRangeLabel } from '@/features/finance/periodFormat'
import { actorLabel } from '@/features/identity/actorName'
import { useActorDirectory } from '@/features/identity/useActorDirectory'

/**
 * Moliyaviy davrlar — ochish, o'chirish va <b>YOPISH</b>.
 *
 * <h2>⚠️ YOPISH — SAHIFADAGI ENG XAVFLI AMAL</h2>
 * U pul yozadi (ledger agregatidan yig'ma provodka) va QAYTARIB
 * BO'LMAYDI: yopilgan davr o'zgarmas, qayta ochish YO'Q. Shuning uchun
 * tugma to'g'ridan-to'g'ri ishlamaydi — `PeriodCloseModal` oqibatni
 * proza bilan yozadi va natijani ko'rsatadi.
 *
 * <h2>⚠️ O'CHIRISH — FAQAT BO'SH VA OCHIQ DAVR</h2>
 * Server yopilgan yoki provodkasi bor davrni rad etadi. Panel tugmani
 * oldindan o'chirib qo'yadi va SABABINI yozadi: "bosolmayapman, nega
 * ekanini bilmayman" holati qolmasin.
 *
 * <h2>⚠️ SAHIFALASH YO'Q — ENDPOINT UNI QO'LLAMAYDI</h2>
 * `GET /api/finance/periods` PARAMETRSIZ (`PeriodController#list`):
 * `page`, `size`, `sort`, `q` yo'q va javob — oddiy massiv. Bunday
 * javobga sahifalash tugmalarini chizish ko'zbo'yamachilik bo'lardi.
 * Davr — OYLIK yozuv, ya'ni ro'yxat yiliga 12 qatordan o'sadi.
 *
 * <h2>⚠️ TARTIB: ENG YANGI DAVR BIRINCHI</h2>
 * `PeriodView` da `createdAt` YO'Q, lekin davr kodi (`YYYY-MM`) va
 * `startsOn` MONOTON: yangi davr doim oldingisidan keyin boshlanadi.
 * Shuning uchun "oxirgi qo'shilgan birinchi" bu yerda `startsOn desc`
 * ga aynan teng. Saralash BUTUN ro'yxatga qo'llanadi (sahifalash yo'q),
 * ya'ni "faqat joriy sahifa saralandi" tuzog'i bu yerda yo'q.
 *
 * <h2>⛔ «YOPILGANLAR QOIDASI» BU SAHIFAGA QO'LLANMAYDI</h2>
 * 2026-08-20 qarori bo'yicha panelning boshqa ro'yxatlarida sukut
 * ko'rinish yopilgan yozuvlarni yashiradi. BU YERDA — YO'Q, va sabab
 * so'zning ma'nosida: `CLOSED` davr «o'chirilgan yozuv» emas,
 * buxgalteriya davrining NORMAL va KUTILGAN yakuniy holati. Oy
 * yopilgani — ishning bajarilgani, o'chirilgani emas.
 *
 * <p>Yopilganlarni yashirish hisobotchining asosiy ish ob'ektini
 * ekrandan olib tashlardi: yopilgan davr eksport qilinadi, u bo'yicha
 * hisobot olinadi va «bu oy yopilganmi?» degan savolga javob AYNAN shu
 * ro'yxatdan chiqadi. Bir necha oydan keyin ro'yxatda bitta ochiq davr
 * qolardi va operator qolganini «yo'qolgan» deb o'qirdi.
 *
 * <p>⚠️ Holat filtri BOR va u ikki tomonga ham ishlaydi — kim faqat
 * ochiq davrni qidirsa `OPEN` ni tanlaydi. Lekin SUKUT — hammasi.
 *
 * <h2>⚠️ "QAYSI VAQT HOLATIGA" (QARORLAR.md §18, 3-band)</h2>
 * Bu sahifadagi sonlar `ledger` sahifasidagi JONLI sonlardan farq
 * qilishi NORMAL: yopilgan davr — muzlatilgan fakt. Buni ekranda ochiq
 * aytmasak, rahbar ikki panelda ikki raqam ko'rib ikkalasiga ham
 * ishonmay qo'yardi.
 */
export function FinancePeriodsPage() {
  const { t } = useTranslation()
  const { modal } = App.useApp()
  const queryClient = useQueryClient()
  const { can } = usePermissions()
  const filter = useLocalListState()

  // ⚠️ N4: `closed_by` ga server `auth.getName()` ni yozadi — ya'ni UUID
  //    yoki login, NOM emas. Xarita va sabab `actorName.ts` da.
  const actors = useActorDirectory()

  const [createOpen, setCreateOpen] = useState(false)
  const [closingPeriod, setClosingPeriod] = useState<Period | null>(null)
  const [error, setError] = useState<string | null>(null)

  const periods = useQuery({
    queryKey: queryKeys.finance.periods,
    queryFn: ({ signal }) => fetchPeriods(signal),
  })

  const rows = useMemo(() => periods.data ?? [], [periods.data])
  const canCreate = can('finance.period.create')
  const canClose = can('finance.period.close')

  const stateFilter = filter.values['state']

  const visible = useMemo(
    () =>
      rows
        .filter(
          (row) =>
            matchesSearch(filter.search, row.code) && matchesValues(stateFilter, row.state),
        )
        .slice()
        // ⚠️ Manba tartibiga TAYANMAYMIZ: `PeriodController#list` da
        // `ORDER BY` shartnomada yozilmagan va u o'zgarsa ekrandagi
        // tartib jimgina buzilardi.
        .sort((a, b) => b.startsOn.localeCompare(a.startsOn)),
    [rows, filter.search, stateFilter],
  )

  const remove = useMutation({
    mutationFn: (id: string) => deletePeriod(id),
    onSuccess: () => {
      setError(null)
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.periods })
    },
    // ⚠️ Xabar sahifa bannerida ko'rinadi va uni niqob to'smaydi
    // (`guards.mjs` 1-bo'lim aynan MODAL ortida qolgan xabar haqida):
    // tasdiq oynasi `onOk` da yopiladi, ya'ni xatoni ko'rsatadigan
    // boshqa yuza qolmaydi.
    onError: (err) => setError(translateError(err, t)),
  })

  const columns: ColumnsType<Period> = [
    {
      // Ro'yxat sahifalanmaydi (`pagination={false}`) — raqam `index` dan
      key: '__rowNo',
      title: t('dataTable.rowNo'),
      width: 56,
      align: 'center',
      render: (_v, _r, index) => <Typography.Text type="secondary">{index + 1}</Typography.Text>,
    },
    {
      key: 'code',
      title: t('finance.period.code'),
      width: 120,
      render: (_, row) => <Typography.Text strong>{row.code}</Typography.Text>,
    },
    {
      key: 'range',
      title: t('finance.period.range'),
      width: 230,
      // ⚠️ `endsOn` EXCLUSIVE — ko'rsatishdan oldin bir kun ayiriladi
      // (`periodFormat.ts`). Aniq lahzalar tooltipda: `Asia/Kabul`
      // UTC+4:30 va oy kunning boshida boshlanmaydi.
      render: (_, row) => (
        <Tooltip title={`${utcTooltip(row.startsAt)}\n\n${utcTooltip(row.endsAt)}`}>
          <span>{periodRangeLabel(row)}</span>
        </Tooltip>
      ),
    },
    {
      key: 'state',
      title: t('finance.period.state'),
      width: 130,
      render: (_, row) =>
        isPeriodClosed(row) ? (
          <Tag icon={<LockOutlined />} color="default">
            {t('periodState.CLOSED')}
          </Tag>
        ) : (
          <Tag color="success">{t('periodState.OPEN')}</Tag>
        ),
    },
    {
      key: 'closedAt',
      title: t('finance.period.closedAt'),
      width: 210,
      render: (_, row) =>
        // ⚠️ BO'SH — «hali yopilmagan», nol yoki xato emas. Ochiq davrda
        // bu maydon bo'lishi mumkin ham emas.
        row.closedAt === null ? (
          t('common.dash')
        ) : (
          <Tooltip title={utcTooltip(row.closedAt)}>
            <span>{formatDateTime(row.closedAt)}</span>
          </Tooltip>
        ),
    },
    {
      key: 'closedBy',
      title: t('finance.period.closedBy'),
      width: 180,
      render: (_, row) => actorLabel(row.closedBy, actors, t('common.dash')),
    },
  ]

  /**
   * ⚠️ YOPILGAN DAVRDA AMAL YASHIRILMAYDI, O'CHIRILADI — sabab sinf
   * javadoc'ida: "bosolmayapman, nega ekanini bilmayman" holati
   * qolmasin. Shuning uchun `hidden` emas, `disabled` + yorliqdagi
   * tooltip: sabab menyuning o'zida qoladi.
   */
  if (canClose || canCreate) {
    columns.push({
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => {
        const closed = isPeriodClosed(row)
        return (
          <RowActions
            items={[
              {
                key: 'close',
                label: closed ? (
                  <Tooltip title={t('finance.period.alreadyClosed')}>
                    <span>{t('finance.close.action')}</span>
                  </Tooltip>
                ) : (
                  t('finance.close.action')
                ),
                onClick: () => setClosingPeriod(row),
                permission: 'finance.period.close',
                disabled: closed,
                danger: true,
              },
              {
                key: 'delete',
                label: closed ? (
                  <Tooltip title={t('finance.period.deleteBlockedClosed')}>
                    <span>{t('actions.delete')}</span>
                  </Tooltip>
                ) : (
                  t('actions.delete')
                ),
                onClick: () => {
                  modal.confirm({
                    title: t('finance.period.deleteTitle'),
                    content: t('finance.period.deleteConfirm', { code: row.code }),
                    okText: t('actions.delete'),
                    okButtonProps: { danger: true },
                    cancelText: t('common.cancel'),
                    onOk: () => remove.mutate(row.id),
                  })
                },
                permission: 'finance.period.create',
                disabled: closed,
                loading: remove.isPending,
                danger: true,
                divider: true,
              },
            ]}
          />
        )
      },
    })
  }

  return (
    <>
      <PageHeader
        title={t('nav.financePeriods')}
        description={t('finance.period.intro')}
        extra={
          <Can permission="finance.period.create">
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
              {t('finance.period.create')}
            </Button>
          </Can>
        }
      />

      {error ? (
        <Alert
          type="error"
          showIcon
          closable
          message={error}
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {periods.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('finance.period.loadFailed')}
          description={translateError(periods.error, t)}
        />
      ) : null}

      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('finance.period.principleTitle')}
        description={t('finance.period.principleBody')}
      />

      {/*
        ⚠️ Eksport DAVRLAR ro'yxatidan YUQORIDA emas, PASTIDA emas —
        jadvaldan oldin: operator avval qaysi davr yopilganini ko'radi,
        keyin uni yuklab oladi. Yopilmagan davrning hisoboti ham
        olinadi va bu to'g'ri (jonli holat), lekin tartib "avval
        holatga qara" degan o'qishni beradi.

        ⚠️ Eksport FILTRDAN mustaqil: u davrni O'ZI tanlaydi va
        jadvaldagi qidiruv unga ta'sir qilmaydi.
      */}
      <FinanceExportCard periods={rows} />

      <Card>
        <ReportFilterBar
          filter={filter}
          searchPlaceholder={t('finance.period.searchPlaceholder')}
          shown={visible.length}
          loaded={rows.length}
          extra={
            <Select
              allowClear
              value={stateFilter?.[0] ?? undefined}
              onChange={(value?: string) => filter.setValues('state', value ? [value] : [])}
              placeholder={t('finance.period.state')}
              style={{ width: 170 }}
              options={PERIOD_STATES.map((value) => ({
                value,
                label: t(`periodState.${value}`),
              }))}
            />
          }
        />

        <Table<Period>
          columns={columns}
          dataSource={visible}
          rowKey={(row) => row.id}
          loading={periods.isPending}
          size="small"
          pagination={false}
          /* 56 + 120 + 230 + 130 + 210 + 180 + 56 = 982 — yig'indidan
             kam bo'lsa antd ustunlarni proporsional SIQADI (X-SIB-011) */
          scroll={{ x: 1000 }}
          locale={{
            emptyText: periods.isError ? (
              <EmptyState variant="error" title={t('finance.period.loadFailed')} />
            ) : (
              <ReportTableEmpty
                filter={filter}
                title={t('finance.period.empty')}
                description={t('finance.period.emptyHint')}
              />
            ),
          }}
        />
      </Card>

      <PeriodCreateModal open={createOpen} periods={rows} onClose={() => setCreateOpen(false)} />

      {/* ⚠️ Davrlar ro'yxati ISHONCHLI bo'lgandagina uzatiladi: oyna
          undan "oldingi davr ochiq" degan xulosa chiqaradi va yiqilgan
          so'rovdan bunday xulosa chiqarish mumkin emas */}
      <PeriodCloseModal
        period={closingPeriod}
        periods={periods.isSuccess ? rows : null}
        onClose={() => setClosingPeriod(null)}
      />
    </>
  )
}
